import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ConfluencePageCreateCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-page-command';
import { ConfluenceUpdatePageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';
import { EditorInsertContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/editor-insert-content-command';
import { ReadContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import {
	RovoStreamAbortedCommand,
	RovoStreamCompletedCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/streaming-lifecycle-commands';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';
import type {
	CommandConstructor,
	CommandProperties,
	CommandResult,
} from '@atlassian/rovo-content-bridge-api/command';

import { AbortCommandHandler } from './command-handlers/abort-command-handler';
import type { ICommandHandler, PassThroughCommandProps } from './command-handlers/command-handler';
import { CreateCommandHandler } from './command-handlers/create-command-handler';
import { InsertContentCommandHandler } from './command-handlers/insert-content-command-handler';
import { PageUpdateCommandHandler } from './command-handlers/page-update-command-handler';
import type { PageUpdateStrategy } from './command-handlers/page-update-strategy';
import { ReadContentCommandHandler } from './command-handlers/read-content-command-handler';
import { AdfActionMetadataStrategy } from './command-handlers/strategies/adf-action-metadata-strategy';
import { AdfUpdateStrategy } from './command-handlers/strategies/adf-update-strategy';
import { DynamicUiUpdateStrategy } from './command-handlers/strategies/dynamic-ui-update-strategy';
import { ImageUpdateStrategy } from './command-handlers/strategies/image-update-strategy';
import { StreamCompletedCommandHandler } from './command-handlers/stream-completed-command-handler';
import { StreamingCommandHandler } from './command-handlers/streaming-command-handler';
import { UpdateCommandHandler } from './command-handlers/update-command-handler';
import { DocumentCreateCommandProcessor } from './stream-processors/create-command-processor';
import { DocumentUpdateCommandProcessor } from './stream-processors/update-command-processor';
import { DocumentDynamicUiUpdateCommandProcessor } from './stream-processors/update-dynamic-ui-command-processor';
import { DocumentImageUpdateCommandProcessor } from './stream-processors/update-image-command-processor';
import {
	AIFC_ADF_UPDATE_KEY,
	DYNAMIC_UI_CREATE_KEY,
	IMAGE_CREATE_KEY,
	AIFC_ACTION_CLASSIFICATION_METADATA,
} from './types/command-manager';
import type { AddCommandHandlersFn, ICommandManager } from './types/command-manager';
import { log } from './utils/aifc-logging';
export class CommandManager implements ICommandManager {
	private commandHandlers: Map<
		CommandConstructor<CommandProperties, CommandResult>,
		ICommandHandler<CommandProperties, CommandResult>
	> = new Map();
	public readonly createCommandProcessor: DocumentCreateCommandProcessor;
	public readonly updateCommandProcessor: DocumentUpdateCommandProcessor;
	public readonly dynamicUiUpdateCommandProcessor?: DocumentDynamicUiUpdateCommandProcessor;
	public readonly imageUpdateCommandProcessor?: DocumentImageUpdateCommandProcessor;

	constructor(
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
		public commandProps: PassThroughCommandProps,
		private addCommandHandlers?: AddCommandHandlersFn | undefined,
	) {
		this.createCommandProcessor = new DocumentCreateCommandProcessor(
			this.commandProps.editorAPI,
			this.commandProps.contentLocation,
		);
		this.updateCommandProcessor = new DocumentUpdateCommandProcessor(
			this.commandProps.editorAPI,
			this.commandProps.contentLocation,
			this.clientId,
			this.rovoBridgeAPI,
		);
		if (fg('platform_editor_maui_edit')) {
			this.dynamicUiUpdateCommandProcessor = new DocumentDynamicUiUpdateCommandProcessor(
				this.commandProps.editorAPI,
			);
			this.imageUpdateCommandProcessor = new DocumentImageUpdateCommandProcessor(
				this.commandProps.onUploadAndInsertMedia,
			);
		}
	}

	/**
	 * Products can use this method to add custom command handlers
	 * they can also add default command handlers using addDefaultCommandHandlers
	 *
	 * @param command - The command constructor
	 * @param handler - The command handler
	 * @returns The command manager
	 */
	addCommandHandler<
		P extends CommandProperties = CommandProperties,
		R extends CommandResult = CommandResult,
	>(command: CommandConstructor<P, R>, handler: ICommandHandler<P, R>): CommandManager {
		this.commandHandlers.set(
			command,
			handler as unknown as ICommandHandler<CommandProperties, CommandResult>,
		);
		return this;
	}

	/**
	 * Products can use this method to add default command handlers
	 * they can also add custom command handlers using addCommandHandler
	 *
	 * @returns The command manager
	 */
	addDefaultCommandHandlers(): CommandManager {
		this.addCreateCommandHandler();
		this.addUpdateCommandHandler();
		this.addReadCommandHandler();
		this.addAbortCommandHandler();
		this.addRovoStreamCompletedCommandHandler();
		if (fg('platform_editor_rovo_bridge_insert_content_command')) {
			this.addInsertContentCommandHandler();
		}
		return this;
	}

	private registerCommandsToRovoBridge() {
		this.commandHandlers.forEach((handler, command) => {
			this.rovoBridgeAPI.on(this.clientId, command, (payload) => {
				return handler.execute(payload);
			});
		});
	}

	/**
	 * Add commands and register them to the rovo bridge
	 */
	addAndRegisterCommands(): void {
		if (this.addCommandHandlers) {
			this.addCommandHandlers({ commandManager: this });
		} else {
			this.addDefaultCommandHandlers();
		}
		this.registerCommandsToRovoBridge();
	}

	private deregisterCommandsFromRovoBridge() {
		this.commandHandlers.forEach((handler, command) => {
			this.rovoBridgeAPI.off(this.clientId, command);
			handler.deregister();
		});
	}

	/**
	 * Deregister all commands
	 */
	deregisterCommands(): void {
		log('deregister commands');

		this.deregisterCommandsFromRovoBridge();
		this.commandHandlers.clear();
	}

	/**
	 * Update clientId
	 */
	updateClientId(clientId: ClientId, rovoBridgeAPI: RovoContentBridgeAPI): void {
		if (this.clientId === clientId && this.rovoBridgeAPI === rovoBridgeAPI) {
			return;
		}

		// add analytics event for the re-registration
		log('update clientId');

		this.deregisterCommandsFromRovoBridge();

		this.clientId = clientId;
		this.rovoBridgeAPI = rovoBridgeAPI;

		this.registerCommandsToRovoBridge();
	}

	/**
	 * Update commandProps
	 */
	updateCommandProps(commandProps: PassThroughCommandProps): void {
		if (this.commandProps === commandProps) {
			return;
		}

		log('update commandProps');

		this.commandProps = commandProps;
		this.commandHandlers.forEach((handler) => {
			handler.updateProps(commandProps);
		});

		if (this.imageUpdateCommandProcessor && commandProps.onUploadAndInsertMedia) {
			this.imageUpdateCommandProcessor.updateCallback(commandProps.onUploadAndInsertMedia);
		}
	}

	/**
	 * Update addCommandHandlers
	 */
	updateAddCommandHandlers(addCommandHandlers?: AddCommandHandlersFn): void {
		if (this.addCommandHandlers === addCommandHandlers) {
			return;
		}

		log('update addCommandHandlers');

		this.addCommandHandlers = addCommandHandlers;
		this.deregisterCommands();
		this.addAndRegisterCommands();
	}

	private addCreateCommandHandler() {
		const createCommandHandler = new CreateCommandHandler(
			this.commandProps,
			this.createCommandProcessor,
		);
		this.addCommandHandler(ConfluencePageCreateCommand, createCommandHandler);
	}

	private addUpdateCommandHandler() {
		if (
			this.dynamicUiUpdateCommandProcessor &&
			this.imageUpdateCommandProcessor &&
			expValEquals('cc-maui-experiment', 'isEnabled', true) &&
			fg('platform_editor_maui_edit')
		) {
			const strategies = new Map<string, PageUpdateStrategy>([
				[AIFC_ADF_UPDATE_KEY, new AdfUpdateStrategy(this.updateCommandProcessor)],
				[DYNAMIC_UI_CREATE_KEY, new DynamicUiUpdateStrategy(this.dynamicUiUpdateCommandProcessor)],
				[IMAGE_CREATE_KEY, new ImageUpdateStrategy(this.imageUpdateCommandProcessor)],
				[
					AIFC_ACTION_CLASSIFICATION_METADATA,
					new AdfActionMetadataStrategy(this.updateCommandProcessor),
				],
			]);

			this.addCommandHandler(
				ConfluenceUpdatePageCommand,
				new PageUpdateCommandHandler(this.commandProps, strategies),
			);
		} else {
			this.addCommandHandler(
				ConfluenceUpdatePageCommand,
				new UpdateCommandHandler(this.commandProps, this.updateCommandProcessor),
			);
		}
	}

	private addReadCommandHandler() {
		const readCommandHandler = new ReadContentCommandHandler(this.commandProps);
		this.addCommandHandler(ReadContentCommand, readCommandHandler);
	}

	private addAbortCommandHandler() {
		const abortCommandHandler = new AbortCommandHandler(this.commandProps, () => {
			this.commandHandlers.forEach((handler) => {
				if (handler instanceof StreamingCommandHandler) {
					handler.handleAbort();
				}
			});
			this.commandProps.onStreamAbort?.();
		});
		this.addCommandHandler(RovoStreamAbortedCommand, abortCommandHandler);
	}

	private addRovoStreamCompletedCommandHandler() {
		const streamCompletedHandler = new StreamCompletedCommandHandler(this.commandProps);
		this.addCommandHandler(RovoStreamCompletedCommand, streamCompletedHandler);
	}

	private addInsertContentCommandHandler() {
		const insertContentCommandHandler = new InsertContentCommandHandler(this.commandProps);
		this.addCommandHandler(EditorInsertContentCommand, insertContentCommandHandler);
	}
}
